// تب دسترسی SCR-STAFF-921 (US-IAM-013): نقش‌ها (فقط نقش‌های قابل تخصیص — BR-IAM-013) و شعب با شعبه پیش‌فرض؛ هشدار آخرین مالک
// (BR-IAM-014، AC-IAM-013.2 ← PHX-IAM-0202): نقش TenantOwner تنها مالک قابل برداشتن نیست. تغییر نقش اثر فوری دارد (Stamp تازه).
// AP-IAM-03 (US-IAM-032، BR-IAM-034): «دسترسی به همه شعب» (شامل شعب آینده — C-AGENT-379)؛ مالک همیشه همه شعب.
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type MembershipView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { applyServerErrors, CheckboxRHF, ErrorState, SelectRHF } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import {
  assignableRoles,
  iamKeys,
  TENANT_OWNER_ROLE,
  useBranchOptions,
  usePermissionCatalog,
  useRoleLabel,
  useRoles,
} from './common';
import { CheckboxGroupRHF } from './InviteDialog';

const rolesSchema = z.object({ roles: z.array(z.string()).min(1, { error: 'iam:invite.roles_required' }) });
type RolesForm = z.input<typeof rolesSchema>;

const branchesSchema = z
  .object({
    branchIds: z.array(z.string()).min(1, { error: 'iam:invite.branches_required' }),
    defaultBranchId: z.string(),
    allBranches: z.boolean(),
  })
  .refine((form) => !form.defaultBranchId || form.branchIds.includes(form.defaultBranchId), {
    path: ['defaultBranchId'],
    error: 'errors:validation.branch_not_in_scope',
  });
type BranchesForm = z.input<typeof branchesSchema>;

function useMembershipSaved(member: MembershipView) {
  const { session } = useTenantSession();
  const queryClient = useQueryClient();
  return (view: MembershipView) => {
    queryClient.setQueryData(iamKeys.membership(session?.tenantId, member.membershipId), view);
    void queryClient.invalidateQueries({ queryKey: iamKeys.all(session?.tenantId) });
  };
}

function RolesSection({ member, canWrite }: { member: MembershipView; canWrite: boolean }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const roleLabel = useRoleLabel();
  const roles = useRoles();
  const catalog = usePermissionCatalog();
  const onSaved = useMembershipSaved(member);
  const [submitError, setSubmitError] = useState<unknown>();
  const [saved, setSaved] = useState(false);
  const isOwner = member.roles.includes(TENANT_OWNER_ROLE);

  // AC-IAM-013.2: آیا این عضو تنها مالک است؟ (همان پیش‌نمایش اثر تعلیق).
  const impact = useQuery({
    queryKey: iamKeys.impact(session?.tenantId, member.membershipId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/identity/memberships/{id}/suspension-impact', {
          params: { path: { id: member.membershipId } },
        }),
      ),
    enabled: isOwner && access.can('iam.member.manage'),
  });
  const lastOwner = isOwner && impact.data?.isLastOwner === true;

  const assignable = useMemo(
    () =>
      new Set(assignableRoles(roles.data?.items ?? [], access.can, catalog.data?.items).map((r) => r.code)),
    [roles.data, access.can, catalog.data],
  );
  const options = (roles.data?.items ?? []).map((role) => ({
    value: role.code,
    label: roleLabel(role.code),
  }));
  // نقش‌های غیرقابل تخصیص (و TenantOwner) غیرفعال‌اند؛ نقش TenantOwner فعلی فقط با انتقال مالکیت (PLT) برداشته می‌شود.
  const disabled = useMemo(
    () =>
      new Set(
        (roles.data?.items ?? [])
          .map((role) => role.code)
          .filter((code) => code === TENANT_OWNER_ROLE || !assignable.has(code)),
      ),
    [roles.data, assignable],
  );

  const values = useMemo<RolesForm>(() => ({ roles: [...member.roles] }), [member.roles]);
  const { control, handleSubmit, formState, setError } = useForm<RolesForm>({
    resolver: zodResolver(rolesSchema),
    values,
  });

  const save = useMutation({
    mutationFn: (body: RolesForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/identity/memberships/{id}/roles', {
          params: { path: { id: member.membershipId } },
          body: { roles: body.roles },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: (view) => {
      onSaved(view);
      setSaved(true);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['roles'],
        codeFields: { 'PHX-IAM-0203': 'roles' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <Box
      component="form"
      noValidate
      data-testid="roles-form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (canWrite) {
          void handleSubmit((form) => save.mutate(form))();
        }
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="h2">{t('iam:access.roles_title')}</Typography>
        {lastOwner && (
          <Alert severity="warning" data-testid="last-owner-warning">
            {t('iam:access.last_owner')}
          </Alert>
        )}
        {saved && (
          <Alert severity="success" data-testid="roles-saved">
            {t('iam:access.roles_saved')}
          </Alert>
        )}
        <CheckboxGroupRHF
          control={control}
          name="roles"
          labelKey="iam:access.roles"
          options={options}
          testId="member-roles"
          disabledValues={disabled}
        />
        <Typography variant="body2" color="text.secondary">
          {t('iam:access.roles_hint')}
        </Typography>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!canWrite || save.isPending || !formState.isDirty}
            data-testid="roles-submit"
          >
            {t('iam:access.save_roles')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

function BranchesSection({ member, canWrite }: { member: MembershipView; canWrite: boolean }) {
  const { t } = useTranslation('iam');
  const { tenantApi } = useTenantSession();
  const { branches } = useBranchOptions();
  const onSaved = useMembershipSaved(member);
  const [submitError, setSubmitError] = useState<unknown>();
  const [saved, setSaved] = useState(false);
  const unnamed = t('shell:branch.unnamed');
  const isOwner = member.roles.includes(TENANT_OWNER_ROLE);

  const values = useMemo<BranchesForm>(
    () => ({
      branchIds: [...member.branchIds],
      defaultBranchId: member.defaultBranchId ?? '',
      allBranches: member.allBranches ?? false,
    }),
    [member.branchIds, member.defaultBranchId, member.allBranches],
  );
  const { control, handleSubmit, formState, setError, setValue } = useForm<BranchesForm>({
    resolver: zodResolver(branchesSchema),
    values,
  });
  const selected = useWatch({ control, name: 'branchIds' });
  const defaultBranch = useWatch({ control, name: 'defaultBranchId' });
  useEffect(() => {
    if (defaultBranch && !selected.includes(defaultBranch)) {
      setValue('defaultBranchId', '', { shouldDirty: true });
    }
  }, [defaultBranch, selected, setValue]);

  const save = useMutation({
    mutationFn: (body: BranchesForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/identity/memberships/{id}/branches', {
          params: { path: { id: member.membershipId } },
          body: {
            branchIds: body.branchIds,
            defaultBranchId: body.defaultBranchId || null,
            allBranches: isOwner ? null : body.allBranches,
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: (view) => {
      onSaved(view);
      setSaved(true);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: ['branchIds', 'defaultBranchId', 'allBranches'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const options = branches.map((branch) => ({ value: branch.id, label: branch.name || unnamed }));

  return (
    <Box
      component="form"
      noValidate
      data-testid="branches-form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (canWrite) {
          void handleSubmit((form) => save.mutate(form))();
        }
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="h2">{t('iam:access.branches_title')}</Typography>
        {saved && (
          <Alert severity="success" data-testid="branches-saved">
            {t('iam:access.branches_saved')}
          </Alert>
        )}
        {isOwner ? (
          <Alert severity="info" data-testid="owner-all-branches">
            {t('iam:access.owner_all_branches')}
          </Alert>
        ) : (
          <Box data-testid="member-all-branches">
            <CheckboxRHF
              control={control}
              name="allBranches"
              labelKey="iam:access.all_branches"
              helperKey="iam:access.all_branches_hint"
              disabled={!canWrite}
            />
          </Box>
        )}
        <CheckboxGroupRHF
          control={control}
          name="branchIds"
          labelKey="iam:access.branches"
          options={options}
          testId="member-branches"
        />
        {selected.length > 0 && (
          <SelectRHF
            control={control}
            name="defaultBranchId"
            labelKey="iam:invite.default_branch"
            helperKey="iam:invite.default_branch_hint"
            options={[
              { value: '', labelKey: 'iam:access.no_default_branch' },
              ...options.filter((option) => selected.includes(option.value)),
            ]}
          />
        )}
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!canWrite || save.isPending || !formState.isDirty}
            data-testid="branches-submit"
          >
            {t('iam:access.save_branches')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export function AccessTab({ member }: { member: MembershipView }) {
  const write = useWriteAccess('iam.member.manage');
  // عضویت لغوشده پایانی است (BR-IAM-016): فقط نمایش.
  const canWrite = write.canWrite && member.status !== 'Revoked';
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="access-tab">
      <Stack spacing={3}>
        <WriteNotice write={write} />
        <RolesSection member={member} canWrite={canWrite} />
        <BranchesSection member={member} canWrite={canWrite} />
      </Stack>
    </Paper>
  );
}
